import { Alert, Button } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { empty } from "../../../../../constants/placeholders"
import { useDateFormat } from "../../../../../hooks/use-date-format.ts"
import { DATE_FORMAT } from "../../../../../utils/date/date.ts"
import { parseLeadDate } from "../../../../Leads/helpers/lead-table-format.ts"
import {
  LEAD_STATUSES_MAP,
  type LeadStatusesValues
} from "../../../constants/lead-statuses.ts"
import { type Lead } from "../../../types/lead.types.ts"

interface LeadArchiveBannerProps {
  lead: Lead
  canRestore?: boolean
  saving?: boolean
  onRestore: () => void
}

/** `LEAD_STATUSES_MAP` holds English display copy, so the label is a key here. */
const stageLabel = (t: (key: string) => string, stage?: number | null) => {
  const label = LEAD_STATUSES_MAP[stage as LeadStatusesValues]?.label
  return label ? t(`leadStages.${label}`) : null
}

/** Why an archived lead is in the Archive, on the page the Archive row opens. */
export function LeadArchiveBanner({
  lead,
  canRestore,
  saving,
  onRestore
}: LeadArchiveBannerProps) {
  const { t } = useTranslation()
  const { userFormat } = useDateFormat()

  if (!lead?.is_archived) return null

  // The API localises dates to the account's own format, so a plain dayjs read
  // turns 07/09/2026 into an invalid day.
  const since = parseLeadDate(lead?.inactive_since || lead?.lost_since, userFormat)
  const previous = stageLabel(t, lead?.previous_stage)

  const facts = [
    lead?.lost_reason &&
      t("leadWorkspace.archive.lossReason", { reason: lead.lost_reason }),
    since &&
      t("leadWorkspace.archive.since", {
        date: since.format(userFormat || DATE_FORMAT)
      }),
    t("leadWorkspace.archive.movedBy", {
      name: lead?.moved_to_inactive_by || empty
    }),
    previous && t("leadWorkspace.archive.previousStage", { stage: previous })
  ].filter(Boolean)

  return (
    <Alert
      type={"info"}
      showIcon
      role={"alert"}
      className={"mb-3"}
      data-testid={"lead-archive-banner"}
      message={t("leadWorkspace.archive.title", {
        reason:
          stageLabel(t, lead?.current_status ?? lead?.status) ??
          t("leadWorkspace.archive.fallback")
      })}
      description={facts.join(" · ")}
      action={
        canRestore ? (
          <Button size={"small"} type={"primary"} loading={saving} onClick={onRestore}>
            {t("leadWorkspace.archive.restore")}
          </Button>
        ) : null
      }
    />
  )
}
